import React, { useRef, useState, useEffect } from 'react';
import * as faceapi from 'face-api.js';
import axios from 'axios';
import { BASE_URL } from '../constants';
import 'bootstrap/dist/css/bootstrap.min.css';

const FaceCaptureModal = ({ onClose, onCapture }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(5); // Start from 5 seconds countdown
  const [faceDetected, setFaceDetected] = useState(false);
  const [modelsLoaded, setModelsLoaded] = useState(false);

  const userData = JSON.parse(localStorage.getItem('userData')) || {};
  const refugeeId = userData?.id;

  useEffect(() => {
    const loadModels = async () => {
      const MODEL_URL = '/models';
      await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
      setModelsLoaded(true);
      startVideo();
    };
    loadModels();
  }, []);

  const startVideo = () => {
    navigator.mediaDevices.getUserMedia({ video: true })
      .then(stream => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      })
      .catch(() => setError('Cannot access camera'));
  };

  // Detect face and start countdown only if face detected
  useEffect(() => {
    if (!modelsLoaded) return;

    const interval = setInterval(async () => {
      if (videoRef.current) {
        const detection = await faceapi.detectSingleFace(
          videoRef.current,
          new faceapi.TinyFaceDetectorOptions()
        );
        if (detection) {
          setFaceDetected(true);
        } else {
          setFaceDetected(false);
          setCountdown(5); // Reset countdown if face lost
        }
      }
    }, 500);

    return () => clearInterval(interval);
  }, [modelsLoaded]);

  // Countdown effect: count down only if face is detected
  useEffect(() => {
    if (!faceDetected) return;

    if (countdown === 0) {
      captureAndSend();
      return;
    }

    const timer = setTimeout(() => {
      setCountdown(c => c - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, faceDetected]);

  const captureAndSend = async () => {
    if (videoRef.current && canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0, 300, 225);
      const imageDataUrl = canvasRef.current.toDataURL('image/jpeg');

      try {
        await axios.put(
          `${BASE_URL}/refugees/${refugeeId}/save-face-image`,
          { faceImage: imageDataUrl },
          { headers: { 'Content-Type': 'application/json' } }
        );

        if (onCapture) onCapture(imageDataUrl);
        onClose();
      } catch (err) {
        console.error('Failed to save face image:', err);
        setError('Failed to save face image');
      }
    }
  };

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content text-center">
          <div className="modal-header justify-content-center">
            <h5 className="modal-title">Align your face with the camera</h5>
          </div>
          <div className="modal-body">
            {error && <div className="alert alert-danger">{error}</div>}

            {faceDetected ? (
              <div
                style={{
                  fontSize: '3rem',
                  fontWeight: 'bold',
                  marginBottom: '10px',
                  color: countdown <= 2 ? 'red' : 'black',
                }}
              >
                {countdown === 0 ? 'Capturing...' : countdown}
              </div>
            ) : (
              <div className="alert alert-warning">No face detected. Please align your face.</div>
            )}

            <video
              ref={videoRef}
              autoPlay
              muted
              width={300}
              height={225}
              className="rounded border"
              style={{ backgroundColor: '#000' }}
            />
            <canvas
              ref={canvasRef}
              width={300}
              height={225}
              style={{ display: 'none' }}
            />
          </div>
          <div className="modal-footer justify-content-center">
            <button onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FaceCaptureModal;
